import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, Clock3, History, Home, LifeBuoy, LogOut, Moon, Plus, Receipt, Sun, Ticket, Undo2, Unplug, UserCog, Wallet, MessageCircle, Loader2, Printer,
} from "lucide-react";
import { api, dt, errMsg, openTab, openWa, rp, waLink } from "@/lib/api";
import { playChime } from "@/lib/sound";
import { InstallButton } from "@/lib/pwa";
import { VoucherCard } from "@/components/VoucherCard";
import AccountProfileForm from "@/components/AccountProfileForm";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-12 rounded-xl bg-secondary/50" {...props} />
  </div>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-border bg-card p-6 ${className}`}>{children}</div>
);

export default function Portal() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [view, setView] = useState("home");
  const [d, setD] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [active, setActive] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [trx, setTrx] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState({ count: 1, profile: "", length: 5, prefix: "", custom_code: "", comment: "" });
  const [batches, setBatches] = useState([]);
  const [vq, setVq] = useState("");            // pencarian kode/batch/catatan
  const [vBatch, setVBatch] = useState("");    // filter batch
  const [vPer, setVPer] = useState("25");      // jumlah tampilan per halaman
  const [vPage, setVPage] = useState(1);
  const [custom, setCustom] = useState(false);
  const [packages, setPackages] = useState([]);
  const [result, setResult] = useState(null);
  const [dep, setDep] = useState({ amount: 50000, note: "" });
  const [ftth, setFtth] = useState({ customer: "", period: new Date().toISOString().slice(0, 7) });
  const [invoice, setInvoice] = useState(null);
  const [notifs, setNotifs] = useState({ unread: 0, items: [] });
  const [report, setReport] = useState({ category: "putus", description: "", phone: "" });
  const [myTickets, setMyTickets] = useState([]);
  const [support, setSupport] = useState({ teknisi_wa: "" });
  const [refund, setRefund] = useState(null); // voucher yang akan direfund
  const [extend, setExtend] = useState(null); // voucher yang akan diperpanjang
  const [extProfile, setExtProfile] = useState("");

  const isMitra = user?.role === "mitra";

  const load = async () => {
    try {
      const [a, b, c, e, f, g, h, tk, sp] = await Promise.all([
        api.get("/portal/dashboard"), api.get("/portal/vouchers"), api.get("/portal/active"),
        api.get("/portal/deposits"), api.get("/portal/transactions"), api.get("/portal/packages"),
        api.get("/portal/notifications"), api.get("/portal/tickets"), api.get("/portal/support"),
      ]);
      setD(a.data); setVouchers(b.data); setActive(c.data); setDeposits(e.data); setTrx(f.data); setPackages(g.data);
      if (g.data.length) setGen((prev) => (prev.profile ? prev : { ...prev, profile: g.data[0].profile }));
      setMyTickets(tk.data); setSupport(sp.data);
      api.get("/portal/voucher-batches").then(({ data: bt }) => setBatches(bt)).catch(() => {});
      if (h.data.unread > notifs.unread && notifs.items.length) {
        playChime("success");
        toast.success("Voucher Anda berhasil login", { description: h.data.items[0]?.message });
      }
      setNotifs(h.data);
    } catch (er) { toast.error(errMsg(er)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);

  const doExtend = async () => {
    if (!extend) return;
    setBusy(true);
    try {
      const { data } = await api.post("/portal/vouchers/extend", {
        username: extend.username,
        profile: extProfile || extend.profile || "",
      });
      toast.success(`${data.username} diperpanjang +${data.added} (total ${data.new_limit}) · saldo -${rp(data.price)}`);
      setExtend(null);
      await load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const doRefund = async () => {
    if (!refund) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/vouchers/${refund.id}/refund`);
      toast.success(`Voucher ${data.username} direfund · saldo +${rp(data.amount)}`);
      setPicked((p) => p.filter((x) => x !== refund.username));
      setRefund(null);
      await load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const createVoucher = async (e) => {
    e.preventDefault(); setBusy(true);    try {
      const { data } = await api.post("/portal/vouchers", {
        ...gen, count: custom ? 1 : Number(gen.count), length: Number(gen.length),
        custom_code: custom ? gen.custom_code.trim() : "",
      });
      setResult(data); toast.success(`${data.vouchers.length} voucher dibuat · saldo ${rp(data.saldo)}`);
      if (custom) setGen((g) => ({ ...g, custom_code: "" }));
      load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const requestDeposit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/portal/deposits", { amount: Number(dep.amount), note: dep.note });
      toast.success("Permintaan saldo terkirim ke admin"); setDep({ amount: 50000, note: "" }); load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const payFtth = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/portal/ftth/pay", { customer: ftth.customer, period: ftth.period });
      setInvoice(data); toast.success(`Pembayaran sukses · ${data.invoice}`); load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const kick = async (a) => {
    try { await api.delete(`/portal/active/${encodeURIComponent(a.id)}`); toast.success(`Koneksi ${a.user} direset`); load(); }
    catch (er) { toast.error(errMsg(er)); }
  };

  const [bill, setBill] = useState(null);
  const [billCode, setBillCode] = useState("");
  const [billHistory, setBillHistory] = useState([]);
  const [receipt, setReceipt] = useState(null);

  const findBill = async (e) => {
    e?.preventDefault(); setBusy(true); setReceipt(null);
    try { const { data } = await api.get(`/portal/ftth/bill?code=${encodeURIComponent(billCode.trim())}`); setBill(data); }
    catch (er) { setBill(null); toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const payBill = async () => {
    if (!window.confirm(`Bayar tagihan ${bill.name} (${bill.customer_code}) sebesar ${rp(bill.amount)}? Saldo Anda akan dipotong.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/portal/ftth/bill/pay", { code: bill.customer_code });
      toast.success(`Lunas · ${data.invoice}`);
      playChime("success");
      setBill(data.bill); setReceipt(data);
      load();
      api.get("/portal/ftth/bill/history").then(({ data: h }) => setBillHistory(h)).catch(() => {});
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const sendReport = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/portal/tickets", report);
      toast.success(`Laporan terkirim · ${data.ticket.code}`);
      playChime("success");
      if (data.teknisi_wa) openWa(data.teknisi_wa, data.wa_message);
      setReport({ category: "putus", description: "", phone: report.phone });
      load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); nav("/login", { replace: true }); };

  // ---- daftar voucher: pencarian, filter batch, dan paginasi ----
  const vQuery = vq.trim().toLowerCase();
  const vFiltered = vouchers.filter(
    (v) =>
      (!vBatch || v.batch === vBatch) &&
      (!vQuery ||
        [v.username, v.batch, v.comment, v.profile].some((x) => String(x || "").toLowerCase().includes(vQuery)))
  );
  const vPerNum = vPer === "all" ? Math.max(vFiltered.length, 1) : Number(vPer);
  const vTotalPages = Math.max(1, Math.ceil(vFiltered.length / vPerNum));
  const vPageSafe = Math.min(Math.max(1, vPage), vTotalPages);
  const vShown = vFiltered.slice((vPageSafe - 1) * vPerNum, vPageSafe * vPerNum);

  const NAVS = [
    { k: "home", label: "Beranda", icon: Home },
    { k: "voucher", label: "Voucher", icon: Ticket },
    { k: "saldo", label: "Saldo", icon: Wallet },
    ...(isMitra ? [{ k: "reset", label: "Reset", icon: Unplug }] : [{ k: "ftth", label: "FTTH", icon: Receipt }]),
    { k: "notif", label: "Notifikasi", icon: Bell },
    { k: "lapor", label: "Lapor", icon: LifeBuoy },
    { k: "tagihan", label: "Tagihan", icon: Receipt },
    { k: "riwayat", label: "Riwayat", icon: History },
  ];

  if (!d) return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Memuat portal…</div>;

  return (
    <div className="grain min-h-screen bg-background pb-28">
      <header className="sticky top-0 z-30 glass border-b border-border px-5 py-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Portal {d.role}</p>
            <h1 className="font-display text-lg font-semibold tracking-tight">{d.name || user?.username}</h1>
          </div>
          <div className="flex items-center gap-1">
            <InstallButton />
            <Button
              size="icon" variant="ghost"
              className={`rounded-full ${view === "akun" ? "text-violet-400" : ""}`}
              onClick={() => setView("akun")} data-testid="portal-account"
              title="Akun Saya"
            >
              <UserCog className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="rounded-full" onClick={toggle} data-testid="portal-theme-toggle">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button size="icon" variant="ghost" className="rounded-full text-destructive" onClick={doLogout} data-testid="portal-logout"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-5 py-8 fade-up" data-testid="portal-content">
        {view === "home" && (
          <>
            <div className="relative overflow-hidden rounded-3xl border border-violet-500/30 bg-gradient-to-br from-cyan-500/15 via-card to-violet-600/20 p-7" data-testid="saldo-card">
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Sisa Saldo</p>
              <p className="mt-3 font-mono text-4xl font-semibold" data-testid="portal-saldo">{rp(d.saldo)}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card><p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Voucher Dibuat</p><p className="mt-2 font-mono text-2xl" data-testid="voucher-total">{d.voucher_total}</p></Card>
              <Card><p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Voucher Aktif</p><p className="mt-2 font-mono text-2xl" data-testid="voucher-active">{d.voucher_active}</p></Card>
              <Card><p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Penjualan Hari Ini</p><p className="mt-2 font-mono text-xl">{rp(d.sales_daily)}</p><p className="text-xs text-muted-foreground">{d.sales_daily_count} voucher</p></Card>
              <Card><p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Bulan Ini</p><p className="mt-2 font-mono text-xl">{rp(d.sales_monthly)}</p></Card>
            </div>
          </>
        )}

        {view === "voucher" && (
          <>
            <Card>
              <h2 className="font-display text-lg font-semibold">Buat Voucher Hotspot</h2>
              <p className="mt-1 text-xs text-muted-foreground">Saldo terpotong sesuai paket. Kode otomatis (huruf besar + angka), username = password, langsung tersinkron ke Mikrotik.</p>
              <form onSubmit={createVoucher} className="mt-5 space-y-4" data-testid="portal-voucher-form">
                {isMitra && !custom && <Field label="Jumlah Voucher" type="number" min="1" max="100" value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })} data-testid="pv-count" />}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paket Voucher</Label>
                  <select
                    className="h-12 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="pv-profile"
                    value={gen.profile} onChange={(e) => setGen({ ...gen, profile: e.target.value })}
                  >
                    {packages.map((p) => (
                      <option key={p.profile || "default"} value={p.profile}>
                        {(p.profile || "default")} — {rp(p.price)}
                      </option>
                    ))}
                  </select>
                  {!packages.length && <p className="text-[11px] text-amber-400" data-testid="pv-no-package">Belum ada paket voucher untuk akun Anda. Hubungi admin.</p>}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Kode Voucher</Label>
                    <Button
                      type="button" size="sm" variant={custom ? "default" : "outline"} className="h-8 rounded-full text-[11px]" data-testid="pv-custom-toggle"
                      onClick={() => { setCustom(!custom); setGen({ ...gen, custom_code: "" }); }}
                    >
                      {custom ? "Pakai Kode Otomatis" : "Custom Code"}
                    </Button>
                  </div>
                  {custom ? (
                    <>
                      <Input
                        required className="h-12 rounded-xl bg-secondary/50 font-mono uppercase" placeholder="mis. ALPHA1" data-testid="pv-custom-code"
                        value={gen.custom_code} onChange={(e) => setGen({ ...gen, custom_code: e.target.value.replace(/[^a-zA-Z0-9]/g, "") })}
                      />
                      <p className="text-[11px] text-muted-foreground">Huruf & angka saja, 1 voucher per pembuatan. Username = password.</p>
                    </>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Kode dibuat otomatis sesuai desain voucher admin.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Catatan / Nama Batch</Label>
                  <Input
                    className="h-12 rounded-xl bg-secondary/50" placeholder="mis. Jualan Warung Bu Sri" data-testid="pv-comment"
                    value={gen.comment} maxLength={60}
                    onChange={(e) => setGen({ ...gen, comment: e.target.value })}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Opsional. Ikut tersimpan sebagai komentar di Mikrotik dan bisa dipakai untuk mencari/menyaring voucher.
                  </p>
                </div>
                <Button type="submit" disabled={busy} data-testid="pv-submit" className="h-12 w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Buat & Potong Saldo`}
                </Button>
              </form>
            </Card>
            {result && (
              <Card>
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold">Voucher {result.batch}</h3>
                  <Button size="sm" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" data-testid="portal-print-batch"
                    onClick={() => openTab(`/portal/cetak?batch=${encodeURIComponent(result.batch)}`)}>
                    <Printer className="mr-1 h-3.5 w-3.5" /> Pilih Ukuran & Cetak
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="portal-print"><Printer className="mr-1 h-3.5 w-3.5" /> Cetak</Button>
                </div>
                <div id="print-area" className="mt-4 grid grid-cols-2 gap-3">
                  {result.vouchers.map((v) => (
                    <VoucherCard key={v.username} v={v} wifi={d.wifi_name} cs={d.cs_wa} note={d.voucher_note} loginUrl={d.hotspot_login_url} price={(d.card_prices || {})[v.profile] ?? d.card_price_default} />
                  ))}
                </div>
              </Card>
            )}
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-display font-semibold">
                  Voucher Saya ({vFiltered.length}{vFiltered.length !== vouchers.length ? ` dari ${vouchers.length}` : ""})
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full text-[11px]" data-testid="pick-all" onClick={() => setPicked(vShown.filter((v) => !v.refunded).map((v) => v.username))}>
                    Pilih Semua
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full text-[11px]" data-testid="pick-clear" onClick={() => setPicked([])}>
                    Bersihkan
                  </Button>
                  <Button
                    size="sm" disabled={!picked.length} data-testid="print-picked"
                    className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white"
                    onClick={() => openTab(`/portal/cetak?codes=${encodeURIComponent(picked.join(","))}`)}
                  >
                    <Printer className="mr-1 h-3.5 w-3.5" /> Cetak Terpilih{picked.length ? ` (${picked.length})` : ""}
                  </Button>
                  {vBatch && (
                    <Button size="sm" variant="outline" className="rounded-full" data-testid="print-batch"
                      onClick={() => openTab(`/portal/cetak?batch=${encodeURIComponent(vBatch)}`)}>
                      <Printer className="mr-1 h-3.5 w-3.5" /> Cetak Batch {vBatch}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" data-testid="portal-print-all"
                    onClick={() => openTab("/portal/cetak")}>
                    <Printer className="mr-1 h-3.5 w-3.5" /> Cetak Semua
                  </Button>
                </div>
              </div>

              {/* pencarian, filter batch, dan jumlah tampilan per halaman */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Input
                  placeholder="Cari kode / batch / catatan…" value={vq} data-testid="v-search"
                  onChange={(e) => { setVq(e.target.value); setVPage(1); }}
                  className="h-10 w-full rounded-full bg-secondary/50 text-xs sm:w-[220px]"
                />
                <select
                  className="h-10 rounded-full border border-input bg-secondary/50 px-3 text-xs" data-testid="v-batch-filter"
                  value={vBatch} onChange={(e) => { setVBatch(e.target.value); setVPage(1); }}
                >
                  <option value="">— semua batch —</option>
                  {batches.map((b) => (
                    <option key={b.batch} value={b.batch}>
                      {b.batch}{b.comment ? ` · ${b.comment}` : ""} ({b.count})
                    </option>
                  ))}
                </select>
                <select
                  className="h-10 rounded-full border border-input bg-secondary/50 px-3 text-xs" data-testid="v-per-page"
                  value={vPer} onChange={(e) => { setVPer(e.target.value); setVPage(1); }}
                >
                  {["10", "25", "50", "100"].map((n) => <option key={n} value={n}>{n} / halaman</option>)}
                  <option value="all">Tampilkan semua</option>
                </select>
              </div>
              <div className="mt-4 space-y-2" data-testid="portal-voucher-list">
                {vShown.map((v) => (
                  <div
                    key={v.id}
                    className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 font-mono text-xs transition-colors duration-200 ${
                      v.refunded
                        ? "border-border/60 bg-secondary/30 opacity-70"
                        : picked.includes(v.username)
                          ? "border-violet-500/60 bg-violet-500/10"
                          : "border-border hover:bg-secondary/40"
                    }`}
                    data-testid={`voucher-row-${v.username}`}
                  >
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox" className="h-4 w-4 accent-violet-500" data-testid={`pick-${v.username}`}
                        disabled={v.refunded}
                        checked={picked.includes(v.username)}
                        onChange={(e) => setPicked((p) => (e.target.checked ? [...p, v.username] : p.filter((x) => x !== v.username)))}
                      />
                      <span className="min-w-0">
                        <span className={`block truncate ${v.refunded ? "line-through" : ""}`}>{v.username} / {v.password}</span>
                        <span className="block truncate text-[10px] text-muted-foreground" data-testid={`voucher-batch-${v.username}`}>
                          {v.batch || "-"}{v.comment ? ` · ${v.comment}` : ""}
                        </span>
                      </span>
                    </label>
                    <span className="text-muted-foreground">{rp(v.price)}</span>
                    <span
                      data-testid={`voucher-status-${v.username}`}
                      className={`rounded-full border px-2 py-0.5 text-[10px] ${
                        v.refunded
                          ? "border-slate-500/40 text-slate-300"
                          : v.is_active
                            ? "border-emerald-500/40 text-emerald-300"
                            : v.used
                              ? "border-amber-500/40 text-amber-300"
                              : "border-cyan-500/40 text-cyan-300"
                      }`}
                    >
                      {v.refunded ? "direfund" : v.is_active ? "sedang aktif" : v.used ? "sudah dipakai" : "belum dipakai"}
                    </span>
                    {!v.refunded && (
                      <Button
                        size="sm" variant="outline" data-testid={`extend-${v.username}`}
                        className="h-8 rounded-full border-cyan-500/40 px-3 text-[11px] text-cyan-300"
                        onClick={() => { setExtProfile(v.profile || (packages[0]?.profile ?? "")); setExtend(v); }}
                      >
                        <Clock3 className="mr-1 h-3.5 w-3.5" /> Perpanjang
                      </Button>
                    )}
                    {v.refundable && (
                      <Button
                        size="sm" variant="outline" data-testid={`refund-${v.username}`}
                        className="h-8 rounded-full border-rose-500/40 px-3 text-[11px] text-rose-300"
                        onClick={() => setRefund(v)}
                      >
                        <Undo2 className="mr-1 h-3.5 w-3.5" /> Refund
                      </Button>
                    )}
                  </div>
                ))}
                {!vFiltered.length && (
                  <p className="text-sm text-muted-foreground" data-testid="v-empty">
                    {vouchers.length ? "Tidak ada voucher yang cocok dengan pencarian/filter." : "Belum ada voucher."}
                  </p>
                )}

                {/* navigasi halaman */}
                {vTotalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2" data-testid="v-pagination">
                    <p className="text-[11px] text-muted-foreground" data-testid="v-page-info">
                      Menampilkan {vShown.length} voucher · halaman {vPageSafe} dari {vTotalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="h-8 rounded-full px-3 text-[11px]" data-testid="v-prev"
                        disabled={vPageSafe <= 1} onClick={() => setVPage(vPageSafe - 1)}>
                        Sebelumnya
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-full px-3 text-[11px]" data-testid="v-next"
                        disabled={vPageSafe >= vTotalPages} onClick={() => setVPage(vPageSafe + 1)}>
                        Berikutnya
                      </Button>
                    </div>
                  </div>
                )}
                <p className="pt-1 text-[11px] text-muted-foreground">
                  Refund hanya bisa untuk voucher yang <b>belum pernah dipakai</b> dan <b>tidak sedang aktif</b>. Kode
                  akan dihapus dari router dan saldo Anda kembali.
                </p>
              </div>
            </Card>
          </>
        )}

        {view === "saldo" && (
          <>
            <Card>
              <h2 className="font-display text-lg font-semibold">Permintaan Saldo</h2>
              <form onSubmit={requestDeposit} className="mt-5 space-y-4" data-testid="deposit-form">
                <Field label="Nominal" type="number" value={dep.amount} onChange={(e) => setDep({ ...dep, amount: e.target.value })} data-testid="dep-amount" />
                <Field label="Catatan / Bukti Transfer" value={dep.note} onChange={(e) => setDep({ ...dep, note: e.target.value })} data-testid="dep-note" />
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button type="submit" disabled={busy} data-testid="dep-submit" className="h-12 flex-1 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">Ajukan</Button>
                  <Button asChild type="button" variant="outline" className="h-12 flex-1 rounded-full">
                    <a href={waLink(d.admin_wa, `Halo Admin, saya ${user?.username} mengajukan top-up saldo ${rp(dep.amount)}. Mohon diproses.`)} target="_blank" rel="noreferrer" data-testid="dep-wa">
                      <MessageCircle className="mr-2 h-4 w-4" /> Hubungi Admin
                    </a>
                  </Button>
                </div>
              </form>
            </Card>
            <Card>
              <h3 className="font-display font-semibold">Riwayat Pengajuan</h3>
              <div className="mt-4 space-y-2" data-testid="deposit-history">
                {deposits.map((x) => (
                  <div key={x.id} className="flex items-center justify-between rounded-xl border border-border px-4 py-3 text-xs">
                    <div><p className="font-mono">{rp(x.amount)}</p><p className="text-muted-foreground">{dt(x.created_at)}</p></div>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${x.status === "pending" ? "border-amber-500/40 text-amber-400" : x.status === "approved" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>{x.status}</span>
                  </div>
                ))}
                {!deposits.length && <p className="text-sm text-muted-foreground">Belum ada pengajuan.</p>}
              </div>
            </Card>
          </>
        )}

        {view === "reset" && (
          <Card>
            <h2 className="font-display text-lg font-semibold">Reset Koneksi Voucher Saya</h2>
            <div className="mt-5 space-y-2" data-testid="portal-active-list">
              {active.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
                  <div className="font-mono text-xs"><p>{a.user}</p><p className="text-muted-foreground">{a.address} · {a.uptime}</p></div>
                  <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => kick(a)} data-testid={`portal-kick-${a.user}`}><Unplug className="mr-1 h-3.5 w-3.5" /> Putuskan</Button>
                </div>
              ))}
              {!active.length && <p className="text-sm text-muted-foreground">Tidak ada voucher Anda yang online.</p>}
            </div>
          </Card>
        )}

        {view === "ftth" && (
          <>
            <Card>
              <h2 className="font-display text-lg font-semibold">Bayar Internet FTTH</h2>
              <p className="mt-1 text-xs text-muted-foreground">Tagihan bulanan {rp(d.ftth_price)} dipotong dari saldo.</p>
              <form onSubmit={payFtth} className="mt-5 space-y-4" data-testid="ftth-form">
                <Field label="Nama Pelanggan / ID" required value={ftth.customer} onChange={(e) => setFtth({ ...ftth, customer: e.target.value })} data-testid="ftth-customer" />
                <Field label="Periode (YYYY-MM)" value={ftth.period} onChange={(e) => setFtth({ ...ftth, period: e.target.value })} data-testid="ftth-period" />
                <Button type="submit" disabled={busy} data-testid="ftth-submit" className="h-12 w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Bayar ${rp(d.ftth_price)}`}
                </Button>
              </form>
            </Card>
            {invoice && (
              <Card data-testid="invoice-card">
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Pembayaran Sukses</p>
                <p className="mt-2 font-mono text-lg">{invoice.invoice}</p>
                <p className="text-sm text-muted-foreground">{rp(invoice.amount)} · sisa saldo {rp(invoice.saldo)}</p>
                <Button asChild className="mt-5 h-12 w-full rounded-full bg-emerald-600 text-white">
                  <a href={waLink(invoice.admin_wa, `Laporan pembayaran FTTH\nInvoice: ${invoice.invoice}\nPelanggan: ${ftth.customer}\nPeriode: ${ftth.period}\nNominal: ${rp(invoice.amount)}\nOleh: ${user?.username}`)} target="_blank" rel="noreferrer" data-testid="invoice-wa">
                    <MessageCircle className="mr-2 h-4 w-4" /> Kirim Invoice ke Admin via WA
                  </a>
                </Button>
              </Card>
            )}
          </>
        )}

        {view === "notif" && (
          <Card>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Notifikasi Voucher</h2>
              {notifs.unread > 0 && (
                <Button size="sm" variant="outline" className="rounded-full" data-testid="notif-read-all"
                  onClick={async () => { await api.post("/portal/notifications/read", {}); load(); }}>
                  Tandai dibaca ({notifs.unread})
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Pemberitahuan saat voucher yang Anda buat berhasil login.</p>
            <div className="mt-5 space-y-2" data-testid="portal-notif-list">
              {notifs.items.map((n) => (
                <div key={n.id} className={`rounded-xl border px-4 py-3 text-xs ${n.read ? "border-border" : "border-violet-400/50 bg-violet-400/5"}`}>
                  <p className="font-mono">{n.message}</p>
                  <p className="mt-1 font-mono text-[10px] text-muted-foreground">{dt(n.created_at)}</p>
                </div>
              ))}
              {!notifs.items.length && <p className="text-sm text-muted-foreground">Belum ada voucher Anda yang login.</p>}
            </div>
          </Card>
        )}

        {view === "lapor" && (
          <>
            <Card>
              <h2 className="font-display text-lg font-semibold">Lapor Gangguan</h2>
              <p className="mt-1 text-xs text-muted-foreground">Laporan Anda langsung menjadi tiket dan memberi notifikasi ke teknisi & admin.</p>
              <form onSubmit={sendReport} className="mt-5 space-y-4" data-testid="report-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Jenis Gangguan</Label>
                  <select
                    className="h-12 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="report-category"
                    value={report.category} onChange={(e) => setReport({ ...report, category: e.target.value })}
                  >
                    {["putus", "lambat", "redaman", "perangkat", "lainnya"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Field label="No. WhatsApp Anda" value={report.phone} onChange={(e) => setReport({ ...report, phone: e.target.value })} data-testid="report-phone" />
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Keluhan</Label>
                  <textarea
                    required className="min-h-[110px] w-full rounded-xl border border-input bg-secondary/50 p-3 text-sm" data-testid="report-description"
                    value={report.description} onChange={(e) => setReport({ ...report, description: e.target.value })}
                  />
                </div>
                <Button type="submit" disabled={busy} data-testid="report-submit" className="h-12 w-full rounded-full bg-gradient-to-r from-amber-500 to-rose-600 text-white">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Laporan"}
                </Button>
              </form>
              {support.teknisi_wa && (
                <Button asChild variant="outline" className="mt-3 h-12 w-full rounded-full" data-testid="report-wa-teknisi">
                  <a href={waLink(support.teknisi_wa, `Halo Teknisi, saya ${user?.username} ingin melaporkan gangguan.`)} target="_blank" rel="noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" /> Chat Teknisi via WhatsApp
                  </a>
                </Button>
              )}
            </Card>
            <Card>
              <h3 className="font-display font-semibold">Laporan Saya ({myTickets.length})</h3>
              <div className="mt-4 space-y-2" data-testid="portal-ticket-list">
                {myTickets.map((t) => (
                  <div key={t.id} className="rounded-xl border border-border px-4 py-3 text-xs" data-testid={`portal-ticket-${t.code}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono">{t.code} · {t.category}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] ${t.status === "selesai" ? "border-emerald-500/40 text-emerald-400" : t.status === "proses" ? "border-sky-500/40 text-sky-400" : "border-amber-500/40 text-amber-400"}`}>{t.status}</span>
                    </div>
                    <p className="mt-1 line-clamp-3 text-muted-foreground">{t.description}</p>
                    <p className="mt-1 font-mono text-[10px] text-muted-foreground">{dt(t.created_at)}{t.assigned_name ? ` · teknisi ${t.assigned_name}` : ""}</p>
                  </div>
                ))}
                {!myTickets.length && <p className="text-sm text-muted-foreground">Belum ada laporan gangguan.</p>}
              </div>
            </Card>
          </>
        )}

        {view === "tagihan" && (
          <>
            <Card>
              <h2 className="font-display text-lg font-semibold">Bayar Tagihan Pelanggan FTTH</h2>
              <p className="mt-1 text-xs text-muted-foreground">Masukkan ID Pelanggan (mis. <span className="font-mono">yanto20</span>) untuk melihat dan membayarkan tagihannya. Saldo Anda dipotong sebesar tagihan.</p>
              <form onSubmit={findBill} className="mt-5 flex gap-2" data-testid="bill-form">
                <Input
                  required className="h-12 rounded-xl bg-secondary/50 font-mono" placeholder="ID Pelanggan" data-testid="bill-code"
                  value={billCode} onChange={(e) => setBillCode(e.target.value)}
                />
                <Button type="submit" disabled={busy} data-testid="bill-search" className="h-12 rounded-full bg-gradient-to-r from-sky-500 to-violet-600 px-6 text-white">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cari"}
                </Button>
              </form>
              {bill && (
                <div className="mt-5 space-y-3 rounded-xl border border-border p-4" data-testid="bill-detail">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-base font-semibold">{bill.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">{bill.customer_code} · {bill.profile || "-"}</p>
                      {bill.address && <p className="text-xs text-muted-foreground">{bill.address}</p>}
                    </div>
                    <span
                      className={`rounded-full border px-3 py-1 text-[10px] uppercase ${bill.paid ? "border-emerald-500/40 text-emerald-400" : bill.status === "isolir" ? "border-red-500/40 text-red-400" : "border-amber-500/40 text-amber-400"}`}
                      data-testid="bill-status"
                    >
                      {bill.paid ? "Lunas" : bill.status === "isolir" ? "Isolir" : "Belum Bayar"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <p>Periode: <b>{bill.period}</b></p>
                    <p>Jatuh tempo: tgl <b>{bill.due_day}</b></p>
                    <p className="col-span-2">Tagihan: <b className="font-mono text-base text-foreground" data-testid="bill-amount">{rp(bill.amount)}</b></p>
                  </div>
                  {!bill.paid ? (
                    <Button onClick={payBill} disabled={busy} data-testid="bill-pay" className="h-12 w-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Bayar ${rp(bill.amount)} dari Saldo`}
                    </Button>
                  ) : (
                    <p className="text-xs text-emerald-400">Tagihan periode ini sudah lunas.</p>
                  )}
                </div>
              )}
              {receipt && (
                <div className="mt-4 space-y-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-xs" data-testid="bill-receipt">
                  <p className="font-mono text-sm">{receipt.invoice}</p>
                  <p>Nominal <b>{rp(receipt.amount)}</b> · periode {receipt.period} · sisa saldo <b>{rp(receipt.saldo)}</b></p>
                  <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="bill-receipt-wa">
                    <a href={waLink(bill?.phone || "", receipt.wa_message)} target="_blank" rel="noreferrer">
                      <MessageCircle className="mr-1 h-3.5 w-3.5" /> Kirim Kuitansi via WhatsApp
                    </a>
                  </Button>
                </div>
              )}
            </Card>
            <Card>
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold">Riwayat Pembayaran yang Saya Terima</h3>
                <Button size="sm" variant="outline" className="rounded-full text-[11px]" data-testid="bill-history-load"
                  onClick={() => api.get("/portal/ftth/bill/history").then(({ data }) => setBillHistory(data)).catch((e) => toast.error(errMsg(e)))}>
                  Muat
                </Button>
              </div>
              <div className="mt-4 space-y-2" data-testid="bill-history">
                {billHistory.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl border border-border px-4 py-3 text-xs">
                    <div>
                      <p className="font-mono">{p.invoice}</p>
                      <p className="text-muted-foreground">{p.customer} · {p.customer_code} · {p.period}</p>
                    </div>
                    <p className="font-mono">{rp(p.amount)}</p>
                  </div>
                ))}
                {!billHistory.length && <p className="text-sm text-muted-foreground">Belum ada data. Tekan Muat untuk menampilkan.</p>}
              </div>
            </Card>
          </>
        )}

        {view === "riwayat" && (
          <Card>
            <h2 className="font-display text-lg font-semibold">Riwayat Transaksi</h2>
            <div className="mt-5 space-y-2" data-testid="portal-trx-list">
              {trx.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-xl border border-border px-4 py-3 text-xs">
                  <div><p>{t.note}</p><p className="font-mono text-muted-foreground">{dt(t.created_at)}</p></div>
                  <span className={`font-mono ${t.amount < 0 ? "text-red-400" : "text-emerald-400"}`}>{rp(t.amount)}</span>
                </div>
              ))}
              {!trx.length && <p className="text-sm text-muted-foreground">Belum ada transaksi.</p>}
            </div>
          </Card>
        )}
        {view === "akun" && <AccountProfileForm />}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-border" data-testid="portal-bottom-nav">
        <div className="mx-auto flex max-w-3xl">
          {NAVS.map(({ k, label, icon: Icon }) => (
            <button
              key={k} onClick={() => setView(k)} data-testid={`bottom-nav-${k}`}
              className={`flex flex-1 flex-col items-center gap-1 py-3 text-[10px] transition-colors duration-200 ${view === k ? "text-violet-400" : "text-muted-foreground"}`}
            >
              <Icon className="h-5 w-5" />
              {label}
            </button>
          ))}
        </div>
      </nav>

      {/* perpanjang masa aktif voucher */}
      {extend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm" data-testid="extend-dialog">
          <div className="w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Perpanjang masa aktif</h3>
            <div className="space-y-1 rounded-xl border border-border bg-secondary/40 p-3 font-mono text-xs">
              <p>Kode: {extend.username}</p>
              <p>Masa aktif sekarang: {extend.limit_uptime || "tanpa batas"}</p>
              <p>Terpakai: {extend.uptime || "0s"}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paket Tambahan</Label>
              <select
                className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="extend-profile"
                value={extProfile} onChange={(e) => setExtProfile(e.target.value)}
              >
                {packages.map((p) => (
                  <option key={p.profile || "default"} value={p.profile}>
                    {(p.profile || "default")} — {rp(p.price)}{p.limit_uptime ? ` / ${p.limit_uptime}` : ""}
                  </option>
                ))}
              </select>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Waktu ditambahkan sesuai paket yang dipilih dan saldo dipotong sebesar harga paket itu. Pengguna
                yang sedang online tidak perlu login ulang atau beli kode baru.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" className="rounded-full" onClick={() => setExtend(null)} data-testid="extend-cancel">Batal</Button>
              <Button className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" onClick={doExtend} disabled={busy || !packages.length} data-testid="extend-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Clock3 className="mr-1.5 h-4 w-4" /> Perpanjang Sekarang</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* konfirmasi refund voucher */}
      {refund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm" data-testid="refund-dialog">
          <div className="w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Refund voucher?</h3>
            <div className="space-y-1 rounded-xl border border-border bg-secondary/40 p-3 font-mono text-xs">
              <p>Kode: {refund.username}</p>
              <p>Harga: {rp(refund.price)}</p>
              <p>Status: belum dipakai</p>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Kode voucher ini akan <b>dihapus dari router</b> dan saldo <b>{rp(refund.price)}</b> dikembalikan ke akun
              Anda. Tindakan ini tidak bisa dibatalkan.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" className="rounded-full" onClick={() => setRefund(null)} data-testid="refund-cancel">Batal</Button>
              <Button className="rounded-full bg-rose-600 text-white hover:bg-rose-700" onClick={doRefund} disabled={busy} data-testid="refund-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Undo2 className="mr-1.5 h-4 w-4" /> Ya, Refund</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
